import { Landmark, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useAccounts } from '../../api'
import type { CardScope } from '../../credit'
import {
  loanBody,
  textKeys,
  useCashAdvanceLimits,
  useCreateLoan,
  useLoanPreview,
  type LoanInput,
  type LoanInstallment,
  type LoanKind,
  type Repayment,
} from '../../loans'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { BankSelect } from '../banks/BankSelect'
import { Field, FormSection } from '../FormBits'

const RATE = /^(\d{1,2}([.,]\d{1,4})?)?$/
const today = () => new Date().toISOString().slice(0, 10)
const nextMonth = () => {
  const d = new Date()
  d.setMonth(d.getMonth() + 1)
  return d.toISOString().slice(0, 10)
}

/**
 * Yeni kredi: tutar, aylık faiz, vade, ilk taksit tarihi, masraflar (kullandırımdan düşülen / ayrıca ödenen).
 * BSMV/KKDF/gecikme oranı boş bırakılırsa kapsamın kredi ayarları kullanılır. Ödeme planı yazarken hesaplanır.
 */
export function LoanForm({ kind, scope, onDone }: { kind: LoanKind; scope: CardScope; onDone: (id?: string) => void }) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys(kind, key)
  const create = useCreateLoan(kind, scope)
  const company = scope === 'company'
  const advance = kind === 'cash_advance'
  // Şirket nakit avansı yalnız nakit avans limiti tanımlı banka hesabından kullanılır.
  const limits = useCashAdvanceLimits(company && advance).data ?? []
  const accounts = (useAccounts().data ?? []).filter((a) => a.active && (a.type === 'cash' || a.type === 'bank'))
  const accountOptions = advance
    ? limits.map((l) => ({
        value: l.accountId,
        label: `${l.name} · ${t('finance.cashAdvances.available', { amount: fmtMoney(l.available) })}`,
      }))
    : accounts.map((a) => ({ value: a.id, label: a.name }))
  const [v, setV] = useState<LoanInput>({
    name: '',
    bankName: '',
    bankId: '',
    note: '',
    principal: '',
    monthlyRate: '',
    repayment: 'installment',
    bsmvRate: '',
    kkdfRate: '',
    lateRate: '',
    installmentCount: 12,
    firstDueDate: nextMonth(),
    disbursedOn: today(),
    disbursementAccountId: '',
    fees: [],
  })
  const [touched, setTouched] = useState(false)
  const set = (patch: Partial<LoanInput>) => setV((x) => ({ ...x, ...patch }))
  const num = (x: string) => Number(x.trim().replace(',', '.'))
  const bullet = v.repayment === 'bullet'
  const limit = limits.find((l) => l.accountId === v.disbursementAccountId)
  const errors = {
    name: v.name.trim().length < 2 ? t(k('form.nameRequired')) : '',
    principal: !(num(v.principal) > 0) ? t('finance.form.amountInvalid') : '',
    rates: [v.monthlyRate, v.bsmvRate, v.kkdfRate, v.lateRate].some((r) => !RATE.test(r.trim()))
      ? t('finance.accounts.pos.rateInvalid')
      : '',
    count: !bullet && !(v.installmentCount >= 1 && v.installmentCount <= 360) ? t(k('form.countInvalid')) : '',
    firstDue: !v.firstDueDate
      ? t(k('form.firstDueRequired'))
      : bullet && v.firstDueDate <= v.disbursedOn
        ? t('finance.cashAdvances.form.dueAfterUse')
        : '',
    limit:
      limit && num(v.principal) > Number(limit.available)
        ? t('finance.cashAdvances.form.overLimit', { amount: fmtMoney(limit.available) })
        : '',
    account: company && !v.disbursementAccountId ? t(k('form.accountRequired')) : '',
    fees: v.fees.some((f) => !f.name.trim() || !(num(f.amount) >= 0)) ? t(k('form.feeInvalid')) : '',
  }
  const valid = Object.values(errors).every((e) => !e)
  const err = (f: keyof typeof errors) => (touched ? errors[f] || undefined : undefined)

  // Önizleme yazmayı bekler (her tuşta istek atılmaz).
  const calcReady = !errors.principal && !errors.rates && !errors.count && !errors.firstDue && !errors.fees
  const body = useMemo(() => (calcReady ? loanBody(v) : null), [calcReady, v])
  const [debounced, setDebounced] = useState(body)
  useEffect(() => {
    const h = setTimeout(() => setDebounced(body), 400)
    return () => clearTimeout(h)
  }, [body])
  const preview = useLoanPreview(kind, scope, debounced)
  const p = preview.data

  return (
    <Modal
      open
      size="2xl"
      onOpenChange={(open) => !open && onDone()}
      dismissible={!create.isPending}
      icon={<Landmark />}
      title={t(k('form.title'))}
      description={t(company ? k('companyHint') : k('personalHint'))}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid) return
        create.mutate(v, {
          onSuccess: (loan) => {
            toast.success(t(k('form.saved')))
            onDone(loan.id)
          },
          onError: toastError,
        })
      }}
      footer={<Footer pending={create.isPending} onCancel={() => onDone()} />}
    >
      <div className="space-y-6">
        <FormSection title={t(k('form.sectionLoan'))}>
          {advance ? (
            <Field id="ln-repay" label={t('finance.cashAdvances.form.repayment')} full>
              <Select
                id="ln-repay"
                value={v.repayment}
                onChange={(r) => set({ repayment: r as Repayment })}
                options={(['installment', 'bullet'] as const).map((r) => ({
                  value: r,
                  label: t(`finance.cashAdvances.repayment.${r}`),
                }))}
              />
            </Field>
          ) : null}
          <Field id="ln-name" label={t(k('form.name'))} error={err('name')}>
            <Input
              id="ln-name"
              maxLength={80}
              placeholder={t(k('form.namePlaceholder'))}
              value={v.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <Field id="ln-bank" label={t('finance.accounts.bank')}>
            <BankSelect id="ln-bank" value={v.bankId} onChange={(bankId) => set({ bankId })} />
          </Field>
          <Field id="ln-principal" label={t(k('form.principal'))} error={err('principal') || err('limit')}>
            <Input
              id="ln-principal"
              inputMode="decimal"
              value={v.principal}
              onChange={(e) => set({ principal: e.target.value })}
            />
          </Field>
          <Field id="ln-rate" label={t(k('form.monthlyRate'))} error={err('rates')} hint={t(k('form.zeroRateHint'))}>
            <Input
              id="ln-rate"
              inputMode="decimal"
              placeholder={advance ? p?.monthlyRate || t('finance.credit.form.defaultRate') : '0'}
              value={v.monthlyRate}
              onChange={(e) => set({ monthlyRate: e.target.value })}
            />
          </Field>
          {bullet ? null : (
            <Field id="ln-count" label={t(k('form.count'))} error={err('count')}>
              <Input
                id="ln-count"
                inputMode="numeric"
                maxLength={3}
                value={String(v.installmentCount || '')}
                onChange={(e) => set({ installmentCount: Number(e.target.value) || 0 })}
              />
            </Field>
          )}
          <Field id="ln-disb" label={t(k('form.disbursedOn'))}>
            <DateField id="ln-disb" value={v.disbursedOn} onChange={(d) => set({ disbursedOn: d })} clearable={false} />
          </Field>
          <Field
            id="ln-first"
            label={t(bullet ? 'finance.cashAdvances.form.dueDate' : k('form.firstDue'))}
            error={err('firstDue')}
          >
            <DateField
              id="ln-first"
              value={v.firstDueDate}
              onChange={(d) => set({ firstDueDate: d })}
              clearable={false}
            />
          </Field>
          {company ? (
            <Field id="ln-acc" label={t(k('form.account'))} error={err('account')} hint={t(k('form.accountHint'))}>
              <Select
                id="ln-acc"
                value={v.disbursementAccountId}
                onChange={(id) => set({ disbursementAccountId: id })}
                placeholder={t('common.select')}
                options={accountOptions}
              />
            </Field>
          ) : null}
          {company && advance && !limits.length ? (
            <p className="text-warning text-xs sm:col-span-2">{t('finance.cashAdvances.noLimitAccounts')}</p>
          ) : null}
        </FormSection>

        <FormSection title={t(k('form.sectionTaxes'))}>
          <p className="text-muted-foreground text-xs sm:col-span-2">{t(k('form.taxesHint'))}</p>
          <Field id="ln-bsmv" label="BSMV %">
            <Input
              id="ln-bsmv"
              inputMode="decimal"
              placeholder={p ? p.bsmvRate : t('finance.credit.form.defaultRate')}
              value={v.bsmvRate}
              onChange={(e) => set({ bsmvRate: e.target.value })}
            />
          </Field>
          <Field id="ln-kkdf" label="KKDF %">
            <Input
              id="ln-kkdf"
              inputMode="decimal"
              placeholder={p ? p.kkdfRate : t('finance.credit.form.defaultRate')}
              value={v.kkdfRate}
              onChange={(e) => set({ kkdfRate: e.target.value })}
            />
          </Field>
          <Field id="ln-late" label={t(k('form.lateRate'))}>
            <Input
              id="ln-late"
              inputMode="decimal"
              placeholder={p ? p.lateRate : t('finance.credit.form.defaultRate')}
              value={v.lateRate}
              onChange={(e) => set({ lateRate: e.target.value })}
            />
          </Field>
        </FormSection>

        <FormSection title={t(k('form.sectionFees'))}>
          <div className="space-y-2 sm:col-span-2">
            {v.fees.map((f, i) => {
              const patch = (x: Partial<typeof f>) =>
                set({ fees: v.fees.map((y, j) => (j === i ? { ...y, ...x } : y)) })
              return (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Input
                    aria-label={t(k('form.feeName'))}
                    className="min-w-40 flex-1"
                    placeholder={t(k('form.feeName'))}
                    value={f.name}
                    onChange={(e) => patch({ name: e.target.value })}
                  />
                  <Input
                    aria-label={t('finance.amount')}
                    className="w-32"
                    inputMode="decimal"
                    placeholder={t('finance.amount')}
                    value={f.amount}
                    onChange={(e) => patch({ amount: e.target.value })}
                  />
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={f.deducted}
                      onChange={(e) => patch({ deducted: e.target.checked })}
                    />
                    {t(k('form.deducted'))}
                  </label>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t('common.delete')}
                    onClick={() => set({ fees: v.fees.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              )
            })}
            {err('fees') ? <p className="text-destructive text-xs">{err('fees')}</p> : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                set({
                  fees: [...v.fees, { name: v.fees.length ? '' : t(k('form.defaultFee')), amount: '', deducted: true }],
                })
              }
            >
              <Plus />
              {t(k('form.addFee'))}
            </Button>
          </div>
        </FormSection>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">{t(k('form.preview'))}</h3>
          {!body ? (
            <p className="text-muted-foreground text-xs">{t(k('form.previewHint'))}</p>
          ) : preview.isError ? (
            <p className="text-destructive text-xs">{(preview.error as Error)?.message}</p>
          ) : !p ? (
            <p className="text-muted-foreground text-xs">{t('common.loading')}</p>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                <Stat label={t(k('installment'))} value={fmtMoney(p.installment)} strong />
                <Stat label={t(k('totalInterest'))} value={fmtMoney(p.totalInterest)} />
                <Stat label={t(k('totalTax'))} value={fmtMoney(p.totalTax)} />
                <Stat label={t(k('totalPayment'))} value={fmtMoney(p.totalPayment)} />
                <Stat label={t(k('netDisbursement'))} value={fmtMoney(p.netDisbursement)} />
                {Number(p.feesSeparate) > 0 ? (
                  <Stat label={t(k('feesSeparate'))} value={fmtMoney(p.feesSeparate)} />
                ) : null}
              </dl>
              <PlanTable rows={p.plan} />
            </>
          )}
        </section>
      </div>
    </Modal>
  )
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={strong ? 'text-sm font-semibold tabular-nums' : 'text-sm tabular-nums'}>{value}</dd>
    </div>
  )
}

export function PlanTable({ rows, action }: { rows: LoanInstallment[]; action?: (row: LoanInstallment) => ReactNode }) {
  const { t } = useTranslation()
  const k = (key: string) => textKeys('loan', key)
  return (
    <div className="max-h-[50vh] overflow-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 sticky top-0 text-left">
          <tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:font-medium">
            <th>#</th>
            <th>{t(k('dueDate'))}</th>
            <th className="text-right">{t(k('principal'))}</th>
            <th className="text-right">{t(k('interest'))}</th>
            <th className="text-right">BSMV</th>
            <th className="text-right">KKDF</th>
            <th className="text-right">{t(k('installment'))}</th>
            <th className="text-right">{t(k('balance'))}</th>
            {action ? <th /> : null}
          </tr>
        </thead>
        <tbody className="divide-y tabular-nums">
          {rows.map((r) => (
            <tr
              key={r.no}
              className={r.overdue ? 'bg-destructive/5 [&>td]:px-2 [&>td]:py-1.5' : '[&>td]:px-2 [&>td]:py-1.5'}
            >
              <td>{r.no}</td>
              <td>{fmtDate(r.dueDate)}</td>
              <td className="text-right">{fmtMoney(r.principal)}</td>
              <td className="text-right">{fmtMoney(r.interest)}</td>
              <td className="text-right">{fmtMoney(r.bsmv)}</td>
              <td className="text-right">{fmtMoney(r.kkdf)}</td>
              <td className="text-right font-medium">{fmtMoney(r.total)}</td>
              <td className="text-right">{fmtMoney(r.balance)}</td>
              {action ? <td className="text-right whitespace-nowrap">{action(r)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
